import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import "@/styles/portfolio.css";
import "@/styles/delivery.css";

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

function DeliveryFooter() {
  return (
    <footer className="delivery-footer">
      <p className="delivery-footer__eu">
        <span className="delivery-footer__eu-flag" aria-hidden>🇪🇺</span>
        Deine Videos liegen sicher auf europäischen Servern (Amsterdam, NL) —
        DSGVO-konform, ohne Umweg über US-Anbieter.
      </p>
      <Link to="/" className="btn btn--ghost delivery-footer__home" data-testid="footer-home">
        <span>Zur Hauptseite</span>
        <span className="btn__arrow">→</span>
      </Link>
      <p className="delivery-footer__brand">
        content.by.max · <a href="https://instagram.com/content.by.max" target="_blank" rel="noopener noreferrer">@content.by.max</a>
      </p>
    </footer>
  );
}

function Loader() {
  return (
    <div className="delivery-shell delivery-shell--center" data-testid="delivery-loader">
      <div className="delivery-loader">
        <span /><span /><span />
      </div>
      <p className="delivery-loader__label">wird geladen</p>
    </div>
  );
}

function State({ eyebrow, title, description, testid }) {
  return (
    <div className="delivery-shell delivery-shell--center" data-testid={testid}>
      <div className="delivery-state">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="display delivery-state__title">{title}</h1>
        <p className="delivery-state__text">{description}</p>
        <Link to="/" className="btn btn--solid" data-testid="state-home-btn">
          <span>Zur Hauptseite</span>
          <span className="btn__arrow">→</span>
        </Link>
      </div>
      <DeliveryFooter />
    </div>
  );
}

function PasswordGate({ slug, clientName, onUnlock }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/drops/${slug}/access`, { password });
      onUnlock(data, password);
    } catch (err) {
      setError(err?.response?.data?.detail || formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="delivery-shell delivery-shell--center" data-testid="password-gate">
      <div className="delivery-lock">
        <span className="eyebrow">Übergabe für {clientName}</span>
        <h1 className="display delivery-state__title">
          Bitte <span className="italic">Passwort</span> eingeben.
        </h1>
        <p className="delivery-state__text">
          Dieser Link ist passwortgeschützt. Max hat dir das Passwort separat mitgeteilt.
        </p>
        <form onSubmit={submit} className="delivery-lock__form" data-testid="password-form">
          <input
            type="password"
            className="admin-input delivery-lock__input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passwort"
            required
            autoFocus
            data-testid="password-input"
          />
          <button type="submit" disabled={busy} className="btn btn--solid" data-testid="password-submit">
            <span>{busy ? "Prüft…" : "Freischalten"}</span>
            <span className="btn__arrow">→</span>
          </button>
        </form>
        {error && <p className="delivery-error" data-testid="password-error">{error}</p>}
      </div>
      <DeliveryFooter />
    </div>
  );
}

function VideoItem({ video, canDownload, slug, password, index }) {
  const [showThanks, setShowThanks] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef(null);

  const startDownload = async () => {
    setDownloading(true);
    try {
      let url = null;
      if (password) {
        await api.post(`/drops/${slug}/download-token`, { password });
      }
      const { data } = await api.get(`/drops/${slug}/download/${index}`);
      url = data.url;
      // Trigger real download
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || video.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setShowThanks(true);
    } catch (err) {
      alert(err?.response?.data?.detail || "Download nicht möglich.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <article className="delivery-video" data-testid={`delivery-video-${index}`}>
      <div className="delivery-video__player">
        <video
          ref={videoRef}
          src={video.url}
          controls
          playsInline
          preload="metadata"
          aria-label={video.filename}
        />
      </div>
      <div className="delivery-video__meta">
        <span className="delivery-video__name">{video.filename}</span>
        <span className="delivery-video__size">{fmtSize(video.size)}</span>
      </div>
      {canDownload && (
        <button
          type="button"
          className="btn btn--solid delivery-video__download"
          onClick={startDownload}
          disabled={downloading}
          data-testid={`download-btn-${index}`}
        >
          <span>{downloading ? "Startet…" : "Original herunterladen"}</span>
          <span className="btn__arrow">↓</span>
        </button>
      )}
      {showThanks && (
        <div className="delivery-thanks" data-testid={`delivery-thanks-${index}`}>
          <span className="italic">Viel Freude damit!</span>
          <p>Bei Fragen einfach melden — oder schau vorbei:</p>
          <Link to="/" className="btn btn--ghost">
            <span>Zur Hauptseite von content.by.max</span>
            <span className="btn__arrow">→</span>
          </Link>
        </div>
      )}
    </article>
  );
}

export default function DeliveryPage() {
  const { slug } = useParams();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [clientName, setClientName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Deine Übergabe · content.by.max";
    (async () => {
      try {
        const { data } = await api.get(`/drops/${slug}`);
        if (data.status === "ready") {
          setData(data);
          setClientName(data.client_name || "");
          setStatus("ready");
        } else if (data.status === "password_required") {
          setClientName(data.client_name || "");
          setStatus("password_required");
        } else {
          setStatus(data.status);
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setStatus("not_found");
        } else {
          setError(formatApiError(err));
          setStatus("error");
        }
      }
    })();
  }, [slug]);

  const onUnlock = (payload, pw) => {
    setData(payload);
    setPassword(pw);
    setStatus("ready");
  };

  if (status === "loading") return <Loader />;
  if (status === "password_required") {
    return <PasswordGate slug={slug} clientName={clientName} onUnlock={onUnlock} />;
  }
  if (status === "expired")
    return (
      <State
        eyebrow="ÜBERGABE"
        title="Dieser Link ist abgelaufen."
        description="Kein Problem — schreib Max kurz, und du bekommst einen neuen frischen Link."
        testid="delivery-expired"
      />
    );
  if (status === "disabled")
    return (
      <State
        eyebrow="ÜBERGABE"
        title="Dieser Link ist nicht mehr verfügbar."
        description="Falls das ein Fehler ist, melde dich einfach bei Max."
        testid="delivery-disabled"
      />
    );
  if (status === "not_found")
    return (
      <State
        eyebrow="ÜBERGABE"
        title="Diese Übergabe existiert nicht."
        description="Prüfe bitte den Link — er ist eventuell nicht ganz vollständig kopiert."
        testid="delivery-notfound"
      />
    );
  if (status === "error")
    return (
      <State
        eyebrow="ÜBERGABE"
        title="Da ist etwas schiefgelaufen."
        description={error || "Bitte in ein paar Minuten erneut versuchen."}
        testid="delivery-error"
      />
    );

  const canDownload = data.access_type === "view_download";

  return (
    <div className="delivery-shell" data-testid="delivery-ready">
      <div className="delivery-hero">
        <span className="eyebrow">DEINE ÜBERGABE</span>
        <h1 className="display delivery-hero__title">
          Hallo <span className="italic">{clientName}</span>.
        </h1>
        {data.message && (
          <p className="delivery-hero__message" data-testid="delivery-message">
            {data.message}
          </p>
        )}
      </div>

      <section className="delivery-videos">
        {data.videos.map((v, i) => (
          <VideoItem
            key={v.url}
            video={v}
            index={i}
            slug={slug}
            password={password}
            canDownload={canDownload}
          />
        ))}
      </section>

      <DeliveryFooter />
    </div>
  );
}
